import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FolderOpen, Trash, Check, X, PencilSimple } from "@phosphor-icons/react";

export const FileManager = ({ clientId, clients, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    if (!clientId) return;
    try {
      const m = await api.manuals(clientId);
      setFiles(m.filter((f) => !f.builtin && f.client_id === clientId));
    } catch { toast.error("Could not load files."); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const rename = async (id) => {
    if (!editName.trim()) { setEditId(null); return; }
    try {
      await api.updateManual(id, { name: editName.trim() });
      setEditId(null); await load(); onChanged?.();
      toast.success("File renamed");
    } catch { toast.error("Rename failed"); }
  };

  const move = async (id, newClientId) => {
    try {
      await api.updateManual(id, { client_id: newClientId });
      await load(); onChanged?.();
      const c = clients.find((x) => x.id === newClientId);
      toast.success(`Moved to "${c?.name || "client"}"`);
    } catch { toast.error("Move failed"); }
  };

  const retag = async (id, category) => {
    try {
      await api.updateManual(id, { category });
      await load(); onChanged?.();
      toast.success(category === "template" ? "Tagged as template (scanning layers…)" : "Tagged as project file");
    } catch { toast.error("Update failed"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.deleteManual(id); await load(); onChanged?.(); toast.success(`Deleted "${name}"`); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="file-manager-btn" disabled={!clientId}
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] text-[var(--text-dim)] hover:text-[var(--primary)] hover:bg-[var(--primary)]/15 transition-colors disabled:opacity-40">
          <FolderOpen size={12} weight="bold" /> Manage
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="file-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><FolderOpen size={18} weight="bold" className="text-[var(--primary)]" />Client File Manager</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Rename, re-tag (project ↔ template), or move files to another client — no re-upload needed.</DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[340px] border border-[var(--border)] rounded-[3px]" data-testid="file-manager-list">
          {files.length === 0 && <div className="p-3 text-[11px] text-[var(--text-dim)]">No files for this client yet. Add some from the Files panel.</div>}
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-2.5 py-2 border-b border-[var(--border)]/40" data-testid={`fm-file-${f.id}`}>
              <div className="min-w-0 flex-1">
                {editId === f.id ? (
                  <div className="flex items-center gap-1">
                    <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") rename(f.id); if (e.key === "Escape") setEditId(null); }}
                      data-testid={`fm-rename-input-${f.id}`}
                      className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
                    <button onClick={() => rename(f.id)} data-testid={`fm-rename-save-${f.id}`} className="text-[var(--success)] hover:opacity-80"><Check size={15} /></button>
                    <button onClick={() => setEditId(null)} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><X size={15} /></button>
                  </div>
                ) : (
                  <div className="text-[13px] truncate flex items-center gap-1.5">
                    {f.name}
                    <button onClick={() => { setEditId(f.id); setEditName(f.name); }} data-testid={`fm-rename-btn-${f.id}`}
                      className="text-[var(--text-dim)] hover:text-[var(--primary)]"><PencilSimple size={12} /></button>
                  </div>
                )}
                <div className="text-[10px] font-mono text-[var(--text-dim)] mt-0.5">
                  {f.type?.toUpperCase()} · {f.num_chunks} sections
                  {f.category === "template" && f.layers?.length > 0 && <span className="text-[var(--success)]"> · ✓ {f.layers.length} layers</span>}
                </div>
              </div>
              <Select value={f.category === "template" ? "template" : "context"} onValueChange={(v) => retag(f.id, v)}>
                <SelectTrigger className="h-7 w-[110px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid={`fm-category-${f.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                  <SelectItem value="context" className="font-mono text-[11px]">Project file</SelectItem>
                  <SelectItem value="template" className="font-mono text-[11px]">Template</SelectItem>
                </SelectContent>
              </Select>
              <Select value={f.client_id || ""} onValueChange={(v) => move(f.id, v)}>
                <SelectTrigger className="h-7 w-[130px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid={`fm-move-${f.id}`}><SelectValue placeholder="Move to…" /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                  {clients.filter((c) => c.id !== clientId).length === 0 && <div className="px-2 py-1.5 text-[11px] text-[var(--text-dim)] font-mono">No other clients</div>}
                  {clients.filter((c) => c.id !== clientId).map((c) => <SelectItem key={c.id} value={c.id} className="font-mono text-[11px]">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={() => del(f.id, f.name)} data-testid={`fm-del-${f.id}`} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><Trash size={14} /></button>
            </div>
          ))}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
